import { useState } from 'react';
import { isEqual } from 'lodash/fp';
import { useScope } from '@mews/framework';
import { PaymentRequestType } from '@mews-commander/data-access';
import { isBillVirtual as isBillVirtualSelector } from '@mews-commander/legacy-entities/src/ducks';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import {
    ProcessPaymentDialogButton,
    ProcessPaymentDialogButtonProps,
} from '../../../payment/components/forms/ProcessPayment/ProcessPaymentDialogButton';
import { getReallBillIdWithoutDelete, replaceVirtualBill } from '../../actions/utils';
import { getAmountDueForBillIds } from '../../selectors';
import { SmartGiftProcessPaymentOption } from './PaymentOptions/smartgift/SmartGiftProcessPaymentOption';

type BillProcessPaymentDialogButtonProps = Omit<
    ProcessPaymentDialogButtonProps,
    'isLoading' | 'onClick' | 'onFormClose'
>;

export const BillProcessPaymentDialogButton = ({
    billId = '',
    accountId,
    ...restProps
}: BillProcessPaymentDialogButtonProps) => {
    const [realBillId, setRealBillId] = useState(billId);
    const isBillVirtual = useCommanderSelector((state) =>
        billId ? isBillVirtualSelector(state, { id: billId }) : false
    );
    const [isLoading, setIsLoading] = useState(false);
    const scope = useScope();
    const dispatch = useCommanderThunkDispatch();

    const enterpriseId = useCurrentEnterpriseId();
    const { billAmounts } = useCommanderSelector(
        (state) =>
            getAmountDueForBillIds(state, {
                enterpriseId,
                billIds: [realBillId],
            }),
        isEqual
    );

    if (!billId || !scope) {
        return null;
    }

    const onClick = async () => {
        if (!isBillVirtual) {
            return;
        }

        setIsLoading(true);
        const realBillIdResponse = await dispatch(getReallBillIdWithoutDelete({ billId, scope }));
        setRealBillId(realBillIdResponse);
        setIsLoading(false);
    };

    const onFormClose = () => {
        if (isBillVirtual) dispatch(replaceVirtualBill({ billId, newBillId: realBillId, scope }));
    };

    return (
        <ProcessPaymentDialogButton
            {...restProps}
            accountId={accountId}
            additionalDropdownOptions={
                realBillId ? <SmartGiftProcessPaymentOption accountId={accountId} billId={realBillId} /> : null
            }
            onFormClose={onFormClose}
            billId={realBillId}
            isLoading={isLoading}
            onClick={onClick}
            allowedPaymentRequestTypeOptions={[PaymentRequestType.Payment]}
            billAmount={
                realBillId
                    ? { value: billAmounts[realBillId].value, currencyCode: billAmounts[realBillId].currency }
                    : undefined
            }
        />
    );
};
